/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import moment from 'moment';
import { CARD_SIZES } from 'carbon-addons-iot-react';

export enum AggregatorType {
  'max' = 'max',
  'min' = 'min',
  'mean' = 'mean',
  'last' = 'last',
  'count' = 'count',
  'first' = 'first',
  'sum' = 'sum',
  'std' = 'std',
}
export interface DataSourceAttribute {
  id: string;
  aggregator?: AggregatorType;
  attribute: string;
  eventName?: string;
}

export type Range = {
  interval: moment.unitOfTime.StartOf | string | moment.unitOfTime.DurationConstructor;
  count: number;
  type: string;
};

export interface DataSource {
  attributes?: DataSourceAttribute[];
  additionalData?: {
    type: string;
    dataFilter?: Record<string, Array<string>>;
  };
  groupBy?: string[];
  range?: Range;
  type?: string;
  timeGrain?: string;
}

export interface AttributeMapType {
  attribute?: string;
  aggregators?: string[];
  timeGrain?: string;
  eventName?: string;
}

// each attribute map is keyed by the datasourceId string
export type AttributeMap = Record<string, AttributeMapType>;

export interface CardAttributes {
  /** dataSourceId points to the id in the dataSource.attributes array */
  dataSourceId: string;
  isDimension: boolean;
  destination?: string;
  label?: string;
  /** dataItemId is the actual attribute name */
  dataItemId: string;
  /** aggregation method will be mapped to the dataSource.attributes[].aggregator section */
  aggregationMethod: string;
  grain?: string;
  dataFilter?: unknown;
  eventName?: string;
}

/** determines the type of hotspot to render. Defaults to 'fixed'. */
export enum HotspotRenderType {
  FIXED = 'fixed',
  DYNAMIC = 'dynamic',
  TEXT = 'text',
}

interface ImageCardHotspots {
  hotspots?: Array<{
    x: string | number;
    y: string | number;
    content: { attributes: CardAttributes[] };
    type: HotspotRenderType;
    locations?: Array<
      | {
          xDataSourceId: string;
          yDataSourceId: string;
        }
      | { x: number; y: number }
    >;
  }>;
}

export interface ColumnsStateType {
  id: string;
  columnName: string;
  isVisible: boolean;
  sortDirection?: string;
}

export enum AlertTableDefaultColumnNames {
  TIMESTAMP = 'timestamp',
  NAME = 'name',
  SOURCE = 'source',
  OWNER = 'owner',
  SEVERITY = 'severity',
  STATUS = 'domainStatus',
  WORKORDER = 'workorder',
  DEVICEID = 'entityId',
  DEVICEALIAS = 'assetAlias',
}

export enum CardTypes {
  VALUE = 'VALUE',
  IMAGE = 'IMAGE',
  TIMESERIES = 'TIMESERIES',
  TABLE = 'TABLE',
  BAR = 'BAR',
  LIST = 'LIST',
  GAUGE = 'GAUGE',
  CUSTOM = 'CUSTOM',
  ALERT = 'ALERT',
  THIRD_PARTY = 'THIRD_PARTY',
}

export enum ThirdPartyContentTypes {
  URL = 'URL',
  DIMENSION = 'DIMENSION',
}

export enum DataItemTypes {
  DERIVED_METRIC = 'DERIVED_METRIC',
  METRIC = 'METRIC',
  DIMENSION = 'DIMENSION',
}

export enum DataItemColumnTypes {
  LITERAL = 'LITERAL',
  BOOLEAN = 'BOOLEAN',
  TIMESTAMP = 'TIMESTAMP',
  NUMBER = 'NUMBER',
}

export interface RenderEditContentReturnType {
  header?: { title?: string; tooltip?: { tooltipText: string; linkText?: string; href?: string } };
  content?: JSX.Element;
}

export interface Card {
  title?: string;
  subtitle?: string;
  hasTitleWrap?: boolean;
  id: string;
  isLoading?: boolean;
  isEmpty?: boolean;
  isEditable?: boolean;
  isExpanded?: boolean;
  isResizable?: boolean;
  hideHeader?: boolean;
  size: CARD_SIZES;
  type: CardTypes;
  content: any; // TODO: specific content type for each card
  values: ImageCardHotspots; // TODO: improve this one to handle union with values
  dataSource: DataSource;
  renderEditContent?: (onChange: CardOnChangeType, card: Card) => RenderEditContentReturnType[];
  renderEditSettings?: (onChange: CardOnChangeType, card: Card) => JSX.Element;
  setupError?: string;
  i18n: Record<string, string>;
  availableActions: Record<string, boolean>;
  hasShortRows: boolean; // only used in alert card to render the shorter version table row
  columnsState?: Array<ColumnsStateType>; // used in alert card
  fontSize?: number; // only used in value card
  customContent?: { type: ThirdPartyContentTypes; value?: string }; // only used in 3rd party content
  /**
   * @deprecated our JSON doesn't support interval, instead we need this in the dataSource.timeGrain
   */
  interval?: string;
  timeRange?: string; // used for manipulating timeGrain but this information also stored in dataSource.range and dataSource.timeGrain after the manupulation.
}

export type CardOnChangeType = (card: Card) => void;

export interface CardWithError extends Card {
  isLoading: boolean;
  setupError: string;
}

export interface LayoutType {
  i?: string;
  x?: number;
  y?: number;
}

export interface DashboardLayoutsType {
  max?: Array<LayoutType>;
  xl?: Array<LayoutType>;
  lg?: Array<LayoutType>;
  md?: Array<LayoutType>;
  sm?: Array<LayoutType>;
  xs?: Array<LayoutType>;
}

export interface DashboardTemplate {
  title: string;
  cards: Array<Card>;
  layouts?: DashboardLayoutsType;
  timeGrain?: string;
}

export interface DashboardTemplateType {
  createdTimestamp?: string;
  dashboardMetaId?: number;
  dashboardMetadataJson: DashboardTemplate;
  dashboardTemplateId?: number;
  dashboardTemplateName?: string;
  isEnabled?: boolean;
  lastUpdatedBy?: string;
  name?: string;
  sequenceNumber?: number;
  summaryDto?: SummaryDto; // only used by V1 Device type summary dashboard
  type?: string;
  updatedTimestamp?: string;
}

export interface SummaryDashboardTemplateType extends DashboardTemplateType {
  summaryDto: SummaryDto;
}

export type AggregationMethods = Record<string, string>;

export interface AggregationInfo {
  inputDataItem: string;
  aggregMethods: AggregationMethods;
  catalogFunction: string;
}

export interface SummaryDto {
  aggregationInfoList: AggregationInfo[];
  granularityName: string;
  granularityTimeFrequency?: string;
  dimensionNames?: string[];
  ready?: boolean;
  hasDashboardMeta?: boolean;
  summaryFilters?: Record<string, Array<string> | Array<number> | Array<boolean>>;
}

interface resourceDto {
  ddId: string;
  description: string;
  name: string;
  resourceId: number;
  status: string;
  type: string;
  updatedBy: string;
  uuid: string;
}

export type DashboardPinType = {
  category: string;
  createTimestamp: string;
  dashboardMetaId: number;
  dashboardPinId: number;
  description: string;
  deviceId: string;
  filterJson: any;
  favorite: boolean;
  homePage: boolean;
  summary: boolean;
  name: string;
  resourceDto: resourceDto;
  thumbnail: string;
  updateTimestamp: string;
};

export interface Threshold {
  axis: string;
  comparison: string;
  fillColor: string;
  label: string;
  value: number;
}

export interface ImageType {
  id: string;
  src: string;
  entity?: string;
  entityTypeId?: string;
}
